/**
 * Where a signed-in visitor belongs when they press a call to action on a
 * PUBLIC page.
 *
 * The site carries THREE independent sessions in three separate stores -
 * customer (`broxel-auth`), partner (`broxel-supplier-storage`) and admin
 * (`broxel-admin-storage`) - so "is this visitor signed in" has no single
 * answer, and any caller that asks would otherwise write its own map. Pure on
 * purpose: no React, no store imports, so the decision can be read and tested
 * without a browser.
 *
 * The CUSTOMER session wins a tie deliberately. `Header` keys its entire
 * signed-in UI off that store alone, so preferring any other session would let
 * the avatar in the header and the button in the hero disagree about who is
 * looking at the same page.
 *
 * A visitor with no session gets the sign-up form. `?mode=signup` is
 * load-bearing, not decoration - `AuthPage` reads exactly that param to open on
 * the register panel, and a bare `/login` would ask a brand-new visitor for a
 * password they do not have yet.
 */

/** The register panel. Mirrors what Header's Register button already links to. */
export const SIGNUP_PATH = '/login?mode=signup'

export interface SessionSnapshot {
  /**
   * `useAuthStore().user?.role`. This store is not customers-only: an admin who
   * signs in through the public `/login` lands here too, and so does a partner,
   * because the backend's login accepts every role.
   */
  customerRole?: 'admin' | 'customer' | 'supplier' | null
  /** `useSupplierStore().isSupplierAuthenticated` - the partner portal session. */
  partnerSignedIn?: boolean
}

/**
 * The signed-in visitor's own home, or null when nobody is signed in.
 *
 * A `supplier` role in the CUSTOMER store is a real, reachable state (signing in
 * at `/login` rather than `/supplier/login`) and is sent to the partner portal
 * even though `SupplierRoute` reads the other store and will ask them to sign in
 * again. That is still the right destination: it is their portal, and its own
 * sign-in page is a far better landing than a sign-up form telling someone who
 * already has an account to create one.
 */
export function signedInDestination(session: SessionSnapshot): string | null {
  const { customerRole, partnerSignedIn } = session

  if (customerRole === 'customer') return '/dashboard'
  if (customerRole === 'admin') return '/admin'
  if (customerRole === 'supplier') return '/supplier'
  if (partnerSignedIn) return '/supplier'

  return null
}

/** The signed-in visitor's home, falling back to the sign-up form. */
export function ctaDestination(session: SessionSnapshot): string {
  return signedInDestination(session) ?? SIGNUP_PATH
}
